import { DatePipe, DecimalPipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import Highcharts from "highcharts";
import { Subscription } from "rxjs";
import scheduleDataJson from "../../../json/re_dsm_meter_data.json";
import { ScheduleBlock, ScheduleUploadRecord } from "../../services/schedule-upload-store.service";

type Generator = {
  generator_id: number;
  name: string;
  qca_id: number;
  installed_capacity_mw: number;
  status: string;
};
type Unit = {
  unit_id: number;
  generator_id: number;
  unit_name: string;
  rated_capacity_mw: number;
};
type Qca = { qca_id: number; name: string; status: string };
type UploadRecord = ScheduleUploadRecord;
type MonitoringRow = ScheduleBlock & {
  deviation: number;
  deviationPercent: number;
  availableCapacity: number;
  cumulativeSchedule: number;
  cumulativeActual: number;
};
type ScheduleData = {
  generators: Generator[];
  generator_units: Unit[];
  qca: Qca[];
};
type ChartXAxisMode = "time" | "block";

@Component({
  selector: "app-view-schedule",
  standalone: true,
  imports: [DatePipe, DecimalPipe],
  templateUrl: "./view-schedule.component.html",
  styleUrls: ["./view-schedule.component.scss"],
})
export class ViewScheduleComponent implements  AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private generatorQueryParamSubscription?: Subscription;
  @ViewChild("blockChart") private blockChartElement?: ElementRef<HTMLElement>;
  @ViewChild("blockChartPanel") private blockChartPanelElement?: ElementRef<HTMLElement>;
  private readonly data = scheduleDataJson as ScheduleData;
  readonly generators = this.data.generators.filter(
    (generator) => generator.status === "Operational",
  );
  readonly qcas = this.data.qca.filter((qca) => qca.status === "Active");
  readonly selectionType = signal<"QCA" | "Generator">("Generator");
  readonly selectedGeneratorId = signal(this.generators[0]?.generator_id ?? 0);
  readonly selectedQcaId = signal(this.qcas[0]?.qca_id ?? 0);
  readonly selectedUnitId = signal<number | null>(null);
  readonly scheduleType = signal("Day Ahead");
  readonly scheduleDate = signal(this.today());
  readonly activeView = signal<"graph" | "table">("graph");
  readonly chartXAxisMode = signal<ChartXAxisMode>("time");
  readonly records = signal<UploadRecord[]>(this.createDemoRecords());
  readonly selectedRecord = signal<UploadRecord | null>(null);

  readonly units = computed(() =>
    this.data.generator_units.filter(
      (unit) => unit.generator_id === this.selectedGeneratorId(),
    ),
  );
  readonly generator = computed(
    () =>
      this.generators.find(
        (item) => item.generator_id === this.selectedGeneratorId(),
      ) ?? null,
  );
  readonly revisionRecords = computed(() =>
    this.recordsForSelectedDate()
      .sort(
        (left, right) =>
          this.revisionIndex(left.revisionNo) -
          this.revisionIndex(right.revisionNo),
      ),
  );
  private readonly refreshChartForRevisions = effect(() => {
    this.revisionRecords();
    if (this.activeView() === "graph") {
      setTimeout(() => this.renderChart());
    }
  });
  readonly record = computed(
    () =>
      this.selectedRecord() ??
      this.matchingRecord() ??
      this.createPreviewRecord(),
  );
  readonly rows = computed<MonitoringRow[]>(() => {
    let cumulativeSchedule = 0;
    let cumulativeActual = 0;
    const cutoffBlock = this.completedBlocksForDate(this.scheduleDate());
    return this.record().scheduleBlocks.map((block) => {
      const schedule = this.isScheduleAvailable(this.scheduleDate())
        ? block.scheduleMw
        : null;
      const actual = block.blockNo <= cutoffBlock
        ? block.actualCapacityMw
        : null;
      const deviation = actual !== null && schedule !== null ? actual - schedule : 0;
      if (schedule !== null) {
        cumulativeSchedule += schedule / 4;
      }
      if (actual !== null) {
        cumulativeActual += actual / 4;
      }
      return {
        ...block,
        scheduleMw: schedule,
        actualCapacityMw: actual,
        deviation,
        deviationPercent:
          schedule !== null ? (deviation / schedule) * 100 : 0,
        availableCapacity: this.availableCapacity(),
        cumulativeSchedule,
        cumulativeActual,
      };
    });
  });
  readonly maxSchedule = computed(() =>
    Math.max(...this.rows().map((row) => row.scheduleMw ?? 0), 0),
  );
  readonly minSchedule = computed(() =>
    Math.min(...this.rows().map((row) => row.scheduleMw ?? 0), 0),
  );
  readonly maxActual = computed(() =>
    Math.max(...this.rows().map((row) => row.actualCapacityMw ?? 0), 0),
  );
  readonly minActual = computed(() =>
    Math.min(...this.rows().map((row) => row.actualCapacityMw ?? 0), 0),
  );
  readonly totalScheduleEnergy = computed(() =>
    this.rows().reduce((sum, row) => sum + (row.scheduleMw ?? 0) / 4, 0),
  );
  readonly totalActualEnergy = computed(() =>
    this.rows().reduce((sum, row) => sum + (row.actualCapacityMw ?? 0) / 4, 0),
  );
  readonly averageDeviation = computed(() =>
    this.rows().length
      ? this.rows().reduce((sum, row) => sum + row.deviation, 0) /
        this.rows().length
      : 0,
  );
  readonly chartSchedule = computed(() =>
    this.chartPoints(this.rows().map((row) => row.scheduleMw ?? 0)),
  );
  readonly chartActual = computed(() =>
    this.chartPoints(this.rows().map((row) => row.actualCapacityMw ?? 0)),
  );
  private blockChart?: Highcharts.Chart;
  private readonly onFullscreenChange = (): void => {
    const isBlockChartFullscreen =
      document.fullscreenElement === this.blockChartPanelElement?.nativeElement;
    if (isBlockChartFullscreen) {
      requestAnimationFrame(() =>
        this.blockChart?.setSize(null, window.innerHeight - 88),
      );
      return;
    }

    // Wait until the browser has returned the panel to its normal-width layout,
    // then recreate Highcharts so no fullscreen-width SVG dimension remains.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.renderChart()),
    );
  };

  constructor(private router: Router) {
    const latest = this.records()[0];
    if (latest) {
      this.selectedGeneratorId.set(latest.generatorId);
      this.selectedUnitId.set(null);
      this.scheduleType.set(latest.scheduleType);
      this.scheduleDate.set(latest.scheduleDate);
    }

    this.generatorQueryParamSubscription = this.route.queryParamMap.subscribe(
      (params) => this.selectGeneratorFromRoute(params.get("generator")),
    );
  }

  ngAfterViewInit(): void {
    this.renderChart();
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  }
  ngOnDestroy(): void {
    this.generatorQueryParamSubscription?.unsubscribe();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.blockChart?.destroy();
  }

  search(): void {
    this.selectedRecord.set(this.matchingRecord());
    this.renderChart();
  }
  reset(): void {
    this.selectedGeneratorId.set(this.generators[0]?.generator_id ?? 0);
    this.selectedUnitId.set(null);
    this.scheduleType.set("Day Ahead");
    this.scheduleDate.set(this.today());
    this.selectedRecord.set(null);
    this.renderChart();
  }
  changeGenerator(value: string): void {
    this.selectedGeneratorId.set(Number(value));
    this.selectedUnitId.set(null);
    this.selectedRecord.set(null);
    setTimeout(() => this.renderChart());
  }

  private selectGeneratorFromRoute(generatorName: string | null): void {
    const generator = this.generators.find(
      (item) => item.name === generatorName?.trim(),
    );
    if (!generator) {
      return;
    }

    this.selectionType.set("Generator");
    this.selectedGeneratorId.set(generator.generator_id);
    this.selectedUnitId.set(null);
    this.selectedRecord.set(null);
  }
  changeSelectionType(value: string): void {
    this.selectionType.set(value === "QCA" ? "QCA" : "Generator");
    this.selectedUnitId.set(null);
    this.selectedRecord.set(null);
  }
  changeQca(value: string): void {
    const qcaId = Number(value);
    this.selectedQcaId.set(qcaId);
    const generator = this.generators.find((item) => item.qca_id === qcaId);
    if (generator) this.selectedGeneratorId.set(generator.generator_id);
    this.selectedUnitId.set(null);
    this.selectedRecord.set(null);
  }
  changeScheduleType(value: string): void {
    this.scheduleType.set(value);
    this.selectedRecord.set(null);
    setTimeout(() => this.renderChart());
  }
  changeScheduleDate(value: string): void {
    this.scheduleDate.set(value);
    this.selectedRecord.set(null);
    setTimeout(() => this.renderChart());
  }
  changeChartXAxisMode(value: string): void {
    this.chartXAxisMode.set(value === "block" ? "block" : "time");
    this.renderChart();
  }
  toggleBlockChartFullscreen(element: HTMLElement): void {
    if (document.fullscreenElement === element) {
      void document.exitFullscreen();
      return;
    }
    void element.requestFullscreen?.();
  }
  resetBlockChartZoom(): void {
    this.blockChart?.zoomOut();
  }
  changeUnit(value: string): void {
    this.selectedUnitId.set(value ? Number(value) : null);
    this.selectedRecord.set(null);
    setTimeout(() => this.renderChart());
  }
  formatTime(block: number): string {
    const start = (block - 1) * 15;
    const end = block * 15;
    return `${this.time(start)} - ${this.time(end)}`;
  }
  maxScheduleBlock(): number {
    return this.blockFor(this.maxSchedule(), "schedule");
  }
  minScheduleBlock(): number {
    return this.blockFor(this.minSchedule(), "schedule");
  }
  maxActualBlock(): number {
    return this.blockFor(this.maxActual(), "actual");
  }
  minActualBlock(): number {
    return this.blockFor(this.minActual(), "actual");
  }
  selectedUnitLabel(): string {
    return this.selectedUnitId()
      ? this.units().find((unit) => unit.unit_id === this.selectedUnitId())
          ?.unit_name || "-"
      : "All generator units";
  }
  setView(view: "graph" | "table"): void {
    this.activeView.set(view);
    if (view === "graph") setTimeout(() => this.renderChart());
  }

  private matchingRecord(): UploadRecord | null {
    return this.revisionRecords()[0] ?? null;
  }
  private recordsForSelectedDate(): UploadRecord[] {
    const selectedDate = this.scheduleDate();
    const matchingFilters = (record: UploadRecord) =>
      record.generatorId === this.selectedGeneratorId() &&
      (this.selectedUnitId() === null ||
        record.unitId === this.selectedUnitId()) &&
      record.scheduleType === this.scheduleType();
    const recordsForDate = this.records().filter(
      (record) => matchingFilters(record) && record.scheduleDate === selectedDate,
    );
    if (recordsForDate.length || selectedDate >= this.today()) {
      return recordsForDate;
    }

    // Demo schedules are stored for today. For a historical selection, retain
    // their revision history while applying the selected past schedule date.
    return this.records()
      .filter((record) => matchingFilters(record) && record.scheduleDate === this.today())
      .map((record) => ({
        ...record,
        id: `${record.id}-${selectedDate}`,
        scheduleDate: selectedDate,
      }));
  }
  private applyRecord(record: UploadRecord): void {
    this.selectedGeneratorId.set(record.generatorId);
    this.selectedUnitId.set(record.unitId);
    this.scheduleType.set(record.scheduleType);
    this.scheduleDate.set(record.scheduleDate);
  }
  private createPreviewRecord(): UploadRecord {
    return {
      id: "preview",
      generatorId: this.selectedGeneratorId(),
      unitId: this.selectedUnitId(),
      scheduleType: this.scheduleType(),
      revisionNo: "Revision-0",
      scheduleDate: this.scheduleDate(),
      fileName: "Preview schedule",
      fileSize: 0,
      uploadedAt: "",
      scheduleBlocks: Array.from({ length: 96 }, (_, index) => {
        const block = index + 1;
        const schedule = this.previewSchedule(block);
        return {
          blockNo: block,
          time: this.time(block * 15),
          scheduleMw: schedule,
          actualCapacityMw: Number(
            (schedule * (0.93 + Math.cos(block / 7) * 0.025)).toFixed(2),
          ),
        };
      }),
    };
  }
  private createDemoRecords(): UploadRecord[] {
    const date = this.today();
    const unitRevisions = this.generators.flatMap((generator) =>
      this.data.generator_units
        .filter((unit) => unit.generator_id === generator.generator_id)
        .slice(0, 2)
        .map((unit, index) => ({
          generatorId: generator.generator_id,
          unitId: unit.unit_id,
          revisions: index === 0 ? 2 : 3,
        })),
    );
    const dayAheadRecords = unitRevisions
      .flatMap(({ generatorId, unitId, revisions }) =>
        Array.from({ length: revisions }, (_, revision) => ({
          id: `demo-unit-${unitId}-revision-${revision}`,
          generatorId,
          unitId,
          scheduleType: "Day Ahead",
          revisionNo: `Revision-${revision}`,
          scheduleDate: date,
          fileName: `GU-${String(unitId).padStart(4, "0")}-R${revision}.xlsx`,
          fileSize: 0,
          uploadedAt: `${date}T00:00:00.000Z`,
          scheduleBlocks: Array.from({ length: 96 }, (_, index) => {
            const block = index + 1;
            const schedule = Number(
              (
                7 +
                unitId * 1.7 +
                revision * 0.35 +
                Math.sin(block / 9) * 1.45
              ).toFixed(2),
            );
            return {
              blockNo: block,
              time: this.time(block * 15),
              scheduleMw: schedule,
              actualCapacityMw: Number(
                (schedule * (0.93 + Math.cos(block / 7) * 0.025)).toFixed(2),
              ),
            };
          }),
        })),
      );
    return (["Day Ahead", "Intraday", "Weekday"] as const).flatMap(
      (scheduleType) =>
        dayAheadRecords.map((record) => ({
          ...record,
          id: `${record.id}-${scheduleType.toLowerCase().replace(" ", "-")}`,
          scheduleType,
        })),
    );
  }
  private availableCapacity(): number {
    return (
      this.units().reduce((sum, unit) => sum + unit.rated_capacity_mw, 0) ||
      this.generator()?.installed_capacity_mw ||
      0
    );
  }
  private previewSchedule(block: number): number {
    const capacity = this.availableCapacity() || 50;
    return (
      Math.round(
        capacity *
          (0.76 +
            Math.sin((block - 4) / 7) * 0.07 +
            Math.sin(block / 2.8) * 0.025) *
          100,
      ) / 100
    );
  }
  private completedBlocksForDate(date: string): number {
    const today = this.today();
    if (date === today) {
      const now = new Date();
      return Math.max(
        0,
        Math.min(96, Math.floor((now.getHours() * 60 + now.getMinutes()) / 15)),
      );
    }
    if (date > today) {
      return 0;
    }
    return 96;
  }
  private isScheduleAvailable(date: string): boolean {
    return date <= this.today();
  }
  private renderChart(): void {
    if (!this.blockChartElement || this.activeView() !== "graph") return;
    this.blockChart?.destroy();
    const revisionRecords = this.revisionRecords();
    const chartRecords = revisionRecords.length ? revisionRecords : [this.record()];
    const chartColors = [
      "#2563eb", "#16a34a", "#7c3aed", "#ea580c",
      "#0891b2", "#e11d48", "#4f46e5", "#ca8a04",
      "#0f766e", "#be123c", "#9333ea", "#0284c7",
    ];
    const latestRevisionByUnit = new Map<number | null, number>();
    for (const record of chartRecords) {
      const revision = this.revisionIndex(record.revisionNo);
      const latest = latestRevisionByUnit.get(record.unitId);
      if (latest === undefined || revision > latest) {
        latestRevisionByUnit.set(record.unitId, revision);
      }
    }
    const series: Highcharts.SeriesOptionsType[] = chartRecords.flatMap(
      (record, index) => {
        const revision = `R${this.revisionIndex(record.revisionNo)}`;
        const unitName =
          this.data.generator_units.find((unit) => unit.unit_id === record.unitId)
            ?.unit_name ?? this.generator()?.name ?? "Schedule";
        const visible =
          this.revisionIndex(record.revisionNo) ===
          latestRevisionByUnit.get(record.unitId);
        return [
          {
            type: "line" as const,
            name: `${unitName}-Sch-${revision}`,
            data: record.scheduleBlocks.map((block) =>
              this.isScheduleAvailable(record.scheduleDate)
                ? block.scheduleMw
                : null,
            ),
            color: chartColors[(index * 2) % chartColors.length],
            lineWidth: 2,
            marker: { enabled: true, radius: 2 },
            visible,
          },
          {
            type: "line" as const,
            name: `${unitName}-Ac-${revision}`,
            data: record.scheduleBlocks.map((block) =>
              block.blockNo <= this.completedBlocksForDate(record.scheduleDate)
                ? block.actualCapacityMw
                : null,
            ),
            color: chartColors[(index * 2 + 1) % chartColors.length],
            lineWidth: 2,
            marker: { enabled: true, radius: 2 },
            visible,
          },
        ];
      },
    );
    const xAxisMode = this.chartXAxisMode();
    const categories = this.rows().map((row) => String(row.blockNo));
    const isTimeMode = xAxisMode === "time";
    this.blockChart = Highcharts.chart(this.blockChartElement.nativeElement, {
      chart: {
        type: "line",
        height: 280,
        zoomType: "x",
        spacing: [12, 14, 8, 10],
        backgroundColor: "transparent",
      },
      title: {
        text: `${isTimeMode ? "Time-wise" : "Block-wise"} Schedule vs Actual (MW)`,
        style: { color: "#173563", fontSize: "12px", fontWeight: "700" },
      },
      xAxis: {
        categories,
        tickInterval: isTimeMode ? 4 : 1,
        tickPositions: isTimeMode
          ? Array.from({ length: 24 }, (_, index) => index * 4)
          : Array.from({ length: categories.length }, (_, index) => index),
        title: { text: isTimeMode ? "Time" : "Block No. (15-min)", style: { fontSize: "10px" } },
        labels: {
          autoRotation: [],
          rotation: isTimeMode ? -45 : 0,
          allowOverlap: !isTimeMode,
          style: { fontSize:  "9px" },
          formatter: function () {
            const index = this.pos as number;
            return isTimeMode
              ? `${String(Math.floor(index / 4)).padStart(2, "0")}:00`
              : String(index + 1);
          },
        },
        lineColor: "#c8d5e8",
      },
      yAxis: {
        min: 0,
        title: { text: "MW", style: { fontSize: "10px" } },
        gridLineColor: "#e8eef6",
        labels: { style: { fontSize: "9px" } },
      },
      tooltip: {
        shared: true,
        formatter: function (this: any) {
          const index = this.points?.[0]?.point?.index ?? 0;
          const start = index * 15;
          const time = (minutes: number) =>
            `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
          const title = isTimeMode
            ? `${time(start)}–${time(start + 15)}`
            : `Block ${index + 1} · ${time(start)}–${time(start + 15)}`;
          let html = `<b>${title}</b><br/>`;
          this.points?.forEach((point: any) => {
            html += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y ?? 0, 2)} MW</b><br/>`;
          });
          return html;
        },
      },
      legend: {
        itemStyle: { fontSize: "10px", fontWeight: "500" },
        symbolWidth: 18,
      },
      credits: { enabled: false },
      series,
    });
  }
  private chartPoints(values: number[]): string {
    const max = Math.max(...values, 1) * 1.1;
    return values
      .map((value, index) => `${(index / 95) * 100},${92 - (value / max) * 78}`)
      .join(" ");
  }
  private blockFor(value: number, key: "schedule" | "actual"): number {
    return (
      this.rows().findIndex(
        (row) =>
          (key === "schedule" ? row.scheduleMw : row.actualCapacityMw) ===
          value,
      ) + 1
    );
  }
  private revisionIndex(revisionNo: string): number {
    const value = Number(revisionNo.replace("Revision-", ""));
    return Number.isFinite(value) ? value : 0;
  }
  private time(totalMinutes: number): string {
    const minutesInDay = totalMinutes % (24 * 60);
    return `${String(Math.floor(minutesInDay / 60)).padStart(2, "0")}:${String(minutesInDay % 60).padStart(2, "0")}`;
  }
  private today(): string {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60_000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 10);
  }

  navigateToScheduleUpload(): void {
    void this.router.navigate(["/upload-schedule"], {
      queryParams: {
        type: this.selectionType(),
        qcaId: this.selectionType() === "QCA" ? this.selectedQcaId() : null,
        generatorId: this.selectedGeneratorId(),
      },
    });
  }
}
